import React, { useState, useEffect } from 'react';
import SuperAdminLayout from '../../../components/hostel/superadmin/SuperAdminLayout';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in';

const ManageRooms = ({ onNavigate, userInfo }) => {
  const [loading, setLoading] = useState(true);
  const [hostels, setHostels] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [selectedHostel, setSelectedHostel] = useState(null);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRoomType, setFilterRoomType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddRoomModal, setShowAddRoomModal] = useState(false);
  const [showEditRoomModal, setShowEditRoomModal] = useState(false);
  const [showEditBedModal, setShowEditBedModal] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedBed, setSelectedBed] = useState(null);
  const [expandedRooms, setExpandedRooms] = useState(new Set());

  const [roomFormData, setRoomFormData] = useState({
    room_number: '',
    floor_number: '',
    block_name: 'A',
    room_type: 'double',
    room_category: '',
    is_ac: false,
    room_facilities: '',
    room_status: 'active',
    total_beds: 2,
    beds: []
  });

  const [bedFormData, setBedFormData] = useState({
    bed_number: '',
    rent_amount: '',
    bed_status: 'available'
  });

  useEffect(() => {
    if (userInfo?.userId) {
      fetchHostels();
    }
  }, [userInfo]);

  useEffect(() => {
    if (selectedHostel) {
      fetchRooms();
    }
  }, [selectedHostel]);

  const fetchHostels = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/superadmin/hostels/${userInfo.userId}`);
      const data = await response.json();

      if (data.success) {
        setHostels(data.hostels || []);
        if (data.hostels && data.hostels.length > 0) {
          setSelectedHostel(data.hostels[0]);
        }
      } else {
        setError(data.message || 'Failed to load hostels');
      }
    } catch (err) {
      console.error('Error fetching hostels:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const fetchRooms = async () => {
    if (!selectedHostel) return;

    try {
      setLoading(true);
      const response = await fetch(
        `${API_URL}/superadmin/rooms/${userInfo.userId}?hostel_id=${selectedHostel.hostel_id}`
      );
      const data = await response.json();

      if (data.success) {
        setRooms(data.rooms || []);
      } else {
        setError(data.message || 'Failed to load rooms');
      }
    } catch (err) {
      console.error('Error fetching rooms:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleRoomTypeChange = (type) => {
    const bedCounts = {
      'single': 1,
      'double': 2,
      'triple': 3,
      'four_sharing': 4,
      'dormitory': 5
    };

    const totalBeds = bedCounts[type] || 2;
    const beds = Array(totalBeds).fill(null).map((_, index) => ({
      bed_number: index + 1,
      rent_amount: '',
      bed_status: 'available'
    }));

    setRoomFormData(prev => ({
      ...prev,
      room_type: type,
      total_beds: totalBeds,
      beds: beds
    }));
  };

  const handleAddRoom = async (e) => {
    e.preventDefault();

    if (!selectedHostel) {
      alert('Please select a hostel first');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/superadmin/room/${userInfo.userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...roomFormData,
          hostel_id: selectedHostel.hostel_id,
          hostel_name: selectedHostel.hostel_name
        })
      });

      const data = await response.json();

      if (data.success) {
        alert('Room added successfully!');
        setShowAddRoomModal(false);
        resetRoomForm();
        fetchRooms();
      } else {
        alert(data.message || 'Failed to add room');
      }
    } catch (err) {
      console.error('Error adding room:', err);
      alert('Failed to add room');
    }
  };

  const handleUpdateRoom = async (e) => {
    e.preventDefault();

    try {
      // Use the first bed's ID as the roomId param to identify the room group
      const roomId = selectedRoom.beds[0].id;
      const response = await fetch(
        `${API_URL}/superadmin/room/${userInfo.userId}/${roomId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            room_type: roomFormData.room_type,
            is_ac: roomFormData.is_ac,
            room_facilities: roomFormData.room_facilities,
            room_status: roomFormData.room_status,
            room_number: roomFormData.room_number,
            block_name: roomFormData.block_name
          })
        }
      );

      const data = await response.json();

      if (data.success) {
        alert('Room updated successfully!');
        setShowEditRoomModal(false);
        setSelectedRoom(null);
        fetchRooms();
      } else {
        alert(data.message || 'Failed to update room');
      }
    } catch (err) {
      console.error('Error updating room:', err);
      alert('Failed to update room');
    }
  };

  const handleUpdateBed = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(
        `${API_URL}/superadmin/bed/${userInfo.userId}/${selectedBed.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(bedFormData)
        }
      );

      const data = await response.json();

      if (data.success) {
        alert('Bed updated successfully!');
        setShowEditBedModal(false);
        setSelectedBed(null);
        fetchRooms();
      } else {
        alert(data.message || 'Failed to update bed');
      }
    } catch (err) {
      console.error('Error updating bed:', err);
      alert('Failed to update bed');
    }
  };

  const handleAddBedToRoom = async (bedNumber, rentAmount) => {
    try {
      const response = await fetch(`${API_URL}/superadmin/bed/${userInfo.userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          hostel_id: selectedRoom.hostel_id || selectedRoom.beds?.[0]?.hostel_id,
          hostel_name: selectedRoom.hostel_name || selectedRoom.beds?.[0]?.hostel_name,
          room_number: roomFormData.room_number,
          floor_number: roomFormData.floor_number,
          block_name: roomFormData.block_name,
          room_type: roomFormData.room_type,
          is_ac: roomFormData.is_ac,
          room_facilities: roomFormData.room_facilities,
          room_status: roomFormData.room_status,
          bed_number: bedNumber,
          rent_amount: rentAmount
        })
      });

      const data = await response.json();

      if (data.success) {
        alert('Bed added successfully!');
        setShowEditRoomModal(false); // Close modal to refresh state from backend
        fetchRooms();
      } else {
        alert(data.message || 'Failed to add bed');
      }
    } catch (err) {
      console.error('Error adding bed:', err);
      alert('Failed to add bed');
    }
  };

  const handleDeleteBedFromRoom = async (bedId) => {
    if (!window.confirm('Are you sure you want to delete this bed?')) {
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/superadmin/bed/${userInfo.userId}/${bedId}`,
        {
          method: 'DELETE'
        }
      );

      const data = await response.json();

      if (data.success) {
        alert('Bed deleted successfully!');
        setShowEditRoomModal(false); // Close modal to refresh state from backend
        fetchRooms();
      } else {
        alert(data.message || 'Failed to delete bed');
      }
    } catch (err) {
      console.error('Error deleting bed:', err);
      alert('Failed to delete bed');
    }
  };

  const handleDeleteRoom = async (roomId) => {
    if (!window.confirm('Are you sure you want to delete this room and all its beds?')) {
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/superadmin/room/${userInfo.userId}/${roomId}`,
        {
          method: 'DELETE'
        }
      );

      const data = await response.json();

      if (data.success) {
        alert('Room deleted successfully!');
        fetchRooms();
      } else {
        alert(data.message || 'Failed to delete room');
      }
    } catch (err) {
      console.error('Error deleting room:', err);
      alert('Failed to delete room');
    }
  };

  const resetRoomForm = () => {
    setRoomFormData({
      room_number: '',
      floor_number: '',
      block_name: 'A',
      room_type: 'double',
      room_category: '',
      is_ac: false,
      room_facilities: '',
      room_status: 'active',
      total_beds: 2,
      beds: [
        { bed_number: 1, rent_amount: '', bed_status: 'available' },
        { bed_number: 2, rent_amount: '', bed_status: 'available' }
      ]
    });
  };

  const openEditRoomModal = (roomGroup) => {
    setSelectedRoom(roomGroup);
    setRoomFormData({
      room_number: roomGroup.room_number,
      floor_number: roomGroup.floor_number,
      block_name: roomGroup.block_name,
      room_type: roomGroup.room_type,
      is_ac: roomGroup.is_ac === 1,
      room_facilities: roomGroup.room_facilities || '',
      room_status: roomGroup.room_status,
      beds: roomGroup.beds // Store all beds for listing
    });
    setShowEditRoomModal(true);
  };

  const openEditBedModal = (bed) => {
    setSelectedBed(bed);
    setBedFormData({
      bed_number: bed.bed_number,
      rent_amount: bed.rent_amount,
      bed_status: bed.bed_status
    });
    setShowEditBedModal(true);
  };

  const toggleRoomExpansion = (roomKey) => {
    const newExpanded = new Set(expandedRooms);
    if (newExpanded.has(roomKey)) {
      newExpanded.delete(roomKey);
    } else {
      newExpanded.add(roomKey);
    }
    setExpandedRooms(newExpanded);
  };

  const filteredRooms = rooms.filter((room) => {
    const matchesSearch = room.room_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
      room.block_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterRoomType === 'all' || room.room_type === filterRoomType;
    const matchesStatus = filterStatus === 'all' || room.room_status === filterStatus;

    return matchesSearch && matchesType && matchesStatus;
  });

  const getRoomStats = () => {
    const totalBeds = rooms.length;
    const availableBeds = rooms.filter(r => r.bed_status === 'available').length;
    const occupiedBeds = rooms.filter(r => r.bed_status === 'occupied').length;
    const maintenanceBeds = rooms.filter(r => r.bed_status === 'maintenance').length;

    return {
      totalRooms: rooms.length,
      totalBeds,
      availableBeds,
      occupiedBeds,
      maintenanceBeds,
      occupancyRate: totalBeds > 0 ? ((occupiedBeds / totalBeds) * 100).toFixed(1) : 0
    };
  };

  const stats = getRoomStats();

  return (
    <SuperAdminLayout
      onNavigate={onNavigate}
      userInfo={userInfo}
      activePage="hostel-superadmin-manage-rooms"
      pageTitle="Manage Rooms 🚪"
    >
      <style>{`
        .hostel-selector-section {
          background: white;
          padding: 24px;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
          margin-bottom: 24px;
        }

        .hostel-selector-label {
          font-size: 14px;
          font-weight: 700;
          color: #2b6777;
          margin-bottom: 12px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .hostel-selector {
          width: 100%;
          padding: 14px 18px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 12px;
          font-size: 16px;
          font-weight: 600;
          color: #2b6777;
          background: white;
          cursor: pointer;
          transition: all 0.3s;
        }

        .hostel-selector:hover {
          border-color: #52ab98;
          background: rgba(82,171,152,0.05);
        }

        .hostel-selector:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 0 0 3px rgba(82,171,152,0.1);
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin-bottom: 32px;
        }

        .stat-card {
          background: white;
          padding: 24px;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
          border: 2px solid rgba(82,171,152,0.1);
          transition: all 0.3s;
        }

        .stat-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 30px rgba(0,0,0,0.12);
          border-color: #52ab98;
        }

        .stat-icon {
          font-size: 32px;
          margin-bottom: 12px;
        }

        .stat-value {
          font-size: 32px;
          font-weight: 900;
          color: #2b6777;
          margin-bottom: 4px;
        }

        .stat-label {
          font-size: 14px;
          color: #6c757d;
          font-weight: 600;
        }

        .controls-section {
          background: white;
          padding: 24px;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
          margin-bottom: 24px;
        }

        .controls-grid {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr auto;
          gap: 16px;
          align-items: end;
        }

        .control-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .control-label {
          font-size: 13px;
          font-weight: 700;
          color: #2b6777;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .search-input, .filter-select {
          padding: 14px 18px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 12px;
          font-size: 15px;
          font-weight: 500;
          transition: all 0.3s;
        }

        .search-input:focus, .filter-select:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 0 0 3px rgba(82,171,152,0.1);
        }

        .add-room-btn {
          padding: 14px 28px;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s;
          box-shadow: 0 4px 16px rgba(43,103,119,0.3);
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .add-room-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 24px rgba(43,103,119,0.4);
        }

        .rooms-section {
          background: white;
          padding: 24px;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
        }

        .room-card {
          background: linear-gradient(135deg, rgba(43,103,119,0.02) 0%, rgba(82,171,152,0.02) 100%);
          border: 2px solid rgba(82,171,152,0.15);
          border-radius: 14px;
          padding: 20px;
          margin-bottom: 16px;
          transition: all 0.3s;
        }

        .room-card:hover {
          border-color: #52ab98;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
        }

        .room-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 16px;
          cursor: pointer;
        }

        .room-info {
          display: flex;
          align-items: center;
          gap: 16px;
          flex: 1;
        }

        .room-icon {
          font-size: 40px;
        }

        .room-details {
          flex: 1;
        }

        .room-title {
          font-size: 20px;
          font-weight: 800;
          color: #2b6777;
          margin-bottom: 4px;
        }

        .room-meta {
          display: flex;
          gap: 16px;
          font-size: 13px;
          color: #6c757d;
          font-weight: 600;
        }

        .room-badge {
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .badge-ac {
          background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
          color: #1976d2;
        }

        .badge-non-ac {
          background: linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%);
          color: #7b1fa2;
        }

        .badge-active {
          background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
          color: #2e7d32;
        }

        .badge-maintenance {
          background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
          color: #e65100;
        }

        .badge-inactive {
          background: linear-gradient(135deg, #fce4ec 0%, #f8bbd0 100%);
          color: #c2185b;
        }

        .room-actions {
          display: flex;
          gap: 8px;
        }

        .icon-btn {
          width: 40px;
          height: 40px;
          border: 2px solid rgba(82,171,152,0.2);
          background: white;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s;
          font-size: 18px;
        }

        .icon-btn:hover {
          background: linear-gradient(135deg, rgba(43,103,119,0.1) 0%, rgba(82,171,152,0.1) 100%);
          border-color: #52ab98;
          transform: scale(1.05);
        }

        .icon-btn.delete-btn:hover {
          background: linear-gradient(135deg, rgba(220,53,69,0.1) 0%, rgba(220,53,69,0.2) 100%);
          border-color: #dc3545;
          color: #dc3545;
        }

        .expand-icon {
          font-size: 20px;
          color: #2b6777;
          transition: transform 0.3s;
        }

        .expand-icon.expanded {
          transform: rotate(180deg);
        }

        .beds-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 16px;
          margin-top: 16px;
          padding-top: 16px;
          border-top: 2px solid rgba(82,171,152,0.1);
        }

        .bed-card {
          background: white;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 12px;
          padding: 18px;
          transition: all 0.3s;
        }

        .bed-card:hover {
          border-color: #52ab98;
          transform: translateY(-2px);
          box-shadow: 0 4px 16px rgba(0,0,0,0.1);
        }

        .bed-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 12px;
        }

        .bed-number {
          font-size: 16px;
          font-weight: 800;
          color: #2b6777;
        }

        .bed-status-badge {
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
        }

        .status-available {
          background: #e8f5e9;
          color: #2e7d32;
        }

        .status-occupied {
          background: #ffebee;
          color: #c62828;
        }

        .status-reserved {
          background: #e3f2fd;
          color: #1565c0;
        }

        .status-maintenance {
          background: #fff3e0;
          color: #e65100;
        }

        .bed-price {
          font-size: 24px;
          font-weight: 900;
          color: #52ab98;
          margin-bottom: 12px;
        }

        .bed-info {
          font-size: 12px;
          color: #6c757d;
          margin-bottom: 12px;
        }

        .bed-actions {
          display: flex;
          gap: 8px;
        }

        .bed-btn {
          flex: 1;
          padding: 10px;
          border: 2px solid rgba(82,171,152,0.2);
          background: white;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 700;
          color: #2b6777;
          cursor: pointer;
          transition: all 0.3s;
        }

        .bed-btn:hover {
          background: linear-gradient(135deg, rgba(43,103,119,0.1) 0%, rgba(82,171,152,0.1) 100%);
          border-color: #52ab98;
        }

        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .modal {
          background: white;
          border-radius: 20px;
          padding: 32px;
          max-width: 800px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
        }

        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 28px;
          padding-bottom: 20px;
          border-bottom: 2px solid rgba(82,171,152,0.15);
        }

        .modal-title {
          font-size: 26px;
          font-weight: 900;
          color: #2b6777;
        }

        .modal-close {
          width: 40px;
          height: 40px;
          border: none;
          background: rgba(43,103,119,0.1);
          border-radius: 10px;
          font-size: 24px;
          color: #2b6777;
          cursor: pointer;
          transition: all 0.3s;
        }

        .modal-close:hover {
          background: rgba(43,103,119,0.2);
          transform: rotate(90deg);
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
          margin-bottom: 24px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .form-group.full-width {
          grid-column: 1 / -1;
        }

        .form-label {
          font-size: 14px;
          font-weight: 700;
          color: #2b6777;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .form-input, .form-select, .form-textarea {
          padding: 14px 18px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 12px;
          font-size: 15px;
          font-weight: 500;
          transition: all 0.3s;
        }

        .form-textarea {
          resize: vertical;
          min-height: 100px;
        }

        .form-input:focus, .form-select:focus, .form-textarea:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 0 0 3px rgba(82,171,152,0.1);
        }

        .checkbox-group {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .checkbox-input {
          width: 24px;
          height: 24px;
          cursor: pointer;
        }

        .beds-section {
          margin-top: 24px;
          padding-top: 24px;
          border-top: 2px solid rgba(82,171,152,0.15);
        }

        .beds-section-title {
          font-size: 18px;
          font-weight: 800;
          color: #2b6777;
          margin-bottom: 16px;
        }

        .bed-form-row {
          display: grid;
          grid-template-columns: 1fr 2fr 1.5fr;
          gap: 16px;
          margin-bottom: 12px;
          padding: 16px;
          background: rgba(82,171,152,0.05);
          border-radius: 12px;
          border: 2px solid rgba(82,171,152,0.1);
        }

        .modal-actions {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
          margin-top: 24px;
        }

        .btn {
          padding: 14px 32px;
          border: none;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s;
        }

        .btn-primary {
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          color: white;
          box-shadow: 0 4px 16px rgba(43,103,119,0.3);
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 24px rgba(43,103,119,0.4);
        }

        .btn-secondary {
          background: white;
          color: #2b6777;
          border: 2px solid rgba(82,171,152,0.2);
        }

        .btn-secondary:hover {
          background: rgba(82,171,152,0.1);
          border-color: #52ab98;
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 400px;
          gap: 20px;
        }

        .spinner {
          width: 60px;
          height: 60px;
          border: 4px solid rgba(82,171,152,0.2);
          border-top-color: #52ab98;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .loading-text {
          font-size: 16px;
          font-weight: 600;
          color: #2b6777;
        }

        .error-container {
          background: linear-gradient(135deg, #fff5f5 0%, #ffe5e5 100%);
          border: 2px solid #ef5350;
          border-radius: 16px;
          padding: 24px;
          text-align: center;
          color: #c62828;
          font-weight: 600;
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
          color: #6c757d;
        }

        .empty-icon {
          font-size: 64px;
          margin-bottom: 16px;
          opacity: 0.5;
        }

        .empty-text {
          font-size: 18px;
          font-weight: 600;
        }

        @media (max-width: 1024px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .controls-grid {
            grid-template-columns: 1fr;
          }
          
          .form-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: 1fr;
          }

          .beds-grid {
            grid-template-columns: 1fr;
          }

          .bed-form-row {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      {loading && !selectedHostel ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <div className="loading-text">Loading hostels...</div>
        </div>
      ) : error ? (
        <div className="error-container">⚠️ {error}</div>
      ) : (
        <>
          <div className="hostel-selector-section">
            <div className="hostel-selector-label">Select Hostel</div>
            <select
              className="hostel-selector"
              value={selectedHostel?.hostel_id || ''}
              onChange={(e) => {
                const hostel = hostels.find(h => h.hostel_id === parseInt(e.target.value));
                setSelectedHostel(hostel);
              }}
            >
              <option value="">Select a hostel...</option>
              {hostels.map(hostel => (
                <option key={hostel.hostel_id} value={hostel.hostel_id}>
                  {hostel.hostel_name} - {hostel.hostel_type}
                </option>
              ))}
            </select>
          </div>

          {selectedHostel && (
            <>
              {/* Statistics Cards */}
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon">🚪</div>
                  <div className="stat-value">{stats.totalRooms}</div>
                  <div className="stat-label">Total Rooms</div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">🛏️</div>
                  <div className="stat-value">{stats.totalBeds}</div>
                  <div className="stat-label">Total Beds</div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">✅</div>
                  <div className="stat-value">{stats.availableBeds}</div>
                  <div className="stat-label">Available Beds</div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">👥</div>
                  <div className="stat-value">{stats.occupiedBeds}</div>
                  <div className="stat-label">Occupied Beds</div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">🔧</div>
                  <div className="stat-value">{stats.maintenanceBeds}</div>
                  <div className="stat-label">Maintenance</div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">📊</div>
                  <div className="stat-value">{stats.occupancyRate}%</div>
                  <div className="stat-label">Occupancy Rate</div>
                </div>
              </div>

              {/* Controls */}
              <div className="controls-section">
                <div className="controls-grid">
                  <div className="control-group">
                    <label className="control-label">Search</label>
                    <input
                      type="text"
                      className="search-input"
                      placeholder="Search by room number or block..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <div className="control-group">
                    <label className="control-label">Room Type</label>
                    <select
                      className="filter-select"
                      value={filterRoomType}
                      onChange={(e) => setFilterRoomType(e.target.value)}
                    >
                      <option value="all">All Types</option>
                      <option value="single">Single</option>
                      <option value="double">Double</option>
                      <option value="triple">Triple</option>
                      <option value="dormitory">Dormitory</option>
                    </select>
                  </div>
                  <div className="control-group">
                    <label className="control-label">Status</label>
                    <select
                      className="filter-select"
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="maintenance">Maintenance</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  <div className="control-group">
                    <label className="control-label">&nbsp;</label>
                    <button className="add-room-btn" onClick={() => {
                      resetRoomForm();
                      setShowAddRoomModal(true);
                    }}>
                      <span>➕</span>
                      <span>Add Room</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Rooms List */}
              <div className="rooms-section">
                {loading ? (
                  <div className="loading-container">
                    <div className="spinner"></div>
                    <div className="loading-text">Loading rooms...</div>
                  </div>
                ) : filteredRooms.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">🚪</div>
                    <div className="empty-text">No rooms found. Add your first room!</div>
                  </div>
                ) : (
                  filteredRooms.map((room) => {
                    const key = `${room.room_number}-${room.floor_number}-${room.block_name}`;
                    const isExpanded = expandedRooms.has(key);
                    return (
                      <div key={key} className="room-card">
                        <div className="room-header" onClick={() => toggleRoomExpansion(key)}>
                          <div className="room-info">
                            <div className="room-icon">🚪</div>
                            <div className="room-details">
                              <div className="room-title">
                                Room {room.room_number} - Block {room.block_name}
                              </div>
                              <div className="room-meta">
                                <span>Floor {room.floor_number}</span>
                                <span>•</span>
                                <span>{room.room_type.charAt(0).toUpperCase() + room.room_type.slice(1)}</span>
                                <span>•</span>
                                <span>{room.beds.length} Beds</span>
                              </div>
                            </div>
                            <div className={`room-badge ${room.is_ac ? 'badge-ac' : 'badge-non-ac'}`}>
                              {room.is_ac ? '❄️ AC' : '🌡️ Non-AC'}
                            </div>
                            <div className={`room-badge badge-${room.room_status}`}>
                              {room.room_status}
                            </div>
                          </div>
                          <div className="room-actions">
                            <button
                              className="icon-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditRoomModal(room);
                              }}
                              title="Edit Room"
                            >
                              ✏️
                            </button>
                            <button
                              className="icon-btn delete-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteRoom(room.beds[0]?.id);
                              }}
                              title="Delete Room"
                            >
                              🗑️
                            </button>
                            <div className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
                              ▼
                            </div>
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="beds-grid">
                            {room.beds.map((bed) => (
                              <div key={bed.id} className="bed-card">
                                <div className="bed-header">
                                  <div className="bed-number">🛏️ Bed {bed.bed_number}</div>
                                  <div className={`bed-status-badge status-${bed.bed_status}`}>
                                    {bed.bed_status}
                                  </div>
                                </div>
                                <div className="bed-price">₹{bed.rent_amount}</div>
                                {bed.student_name && (
                                  <div className="bed-info">
                                    👤 {bed.student_name}<br />
                                    📱 {bed.student_phone}
                                  </div>
                                )}
                                <div className="bed-actions">
                                  <button
                                    className="bed-btn"
                                    onClick={() => openEditBedModal(bed)}
                                  >
                                    ✏️ Edit
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* Add Room Modal */}
      {showAddRoomModal && (
        <div className="modal-overlay" onClick={() => setShowAddRoomModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">➕ Add New Room</h2>
              <button className="modal-close" onClick={() => setShowAddRoomModal(false)}>×</button>
            </div>
            <form onSubmit={handleAddRoom}>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Room Number *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={roomFormData.room_number}
                    onChange={(e) => setRoomFormData({ ...roomFormData, room_number: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Floor Number *</label>
                  <input
                    type="number"
                    className="form-input"
                    value={roomFormData.floor_number}
                    onChange={(e) => setRoomFormData({ ...roomFormData, floor_number: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Block Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={roomFormData.block_name}
                    onChange={(e) => setRoomFormData({ ...roomFormData, block_name: e.target.value })}
                    maxLength="10"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Room Type *</label>
                  <select
                    className="form-select"
                    value={roomFormData.room_type}
                    onChange={(e) => handleRoomTypeChange(e.target.value)}
                    required
                  >
                    <option value="single">Single (1 Bed)</option>
                    <option value="double">Double (2 Beds)</option>
                    <option value="triple">Triple (3 Beds)</option>
                    <option value="four_sharing">Four Sharing (4 Beds)</option>
                    <option value="dormitory">Dormitory (5+ Beds)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Room Category</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g., Deluxe, Standard"
                    value={roomFormData.room_category}
                    onChange={(e) => setRoomFormData({ ...roomFormData, room_category: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Room Status *</label>
                  <select
                    className="form-select"
                    value={roomFormData.room_status}
                    onChange={(e) => setRoomFormData({ ...roomFormData, room_status: e.target.value })}
                    required
                  >
                    <option value="active">Active</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div className="form-group full-width">
                  <label className="form-label">Room Facilities</label>
                  <textarea
                    className="form-textarea"
                    placeholder="e.g., Attached Bathroom, Study Table, Wardrobe"
                    value={roomFormData.room_facilities}
                    onChange={(e) => setRoomFormData({ ...roomFormData, room_facilities: e.target.value })}
                  />
                </div>
                <div className="form-group full-width">
                  <div className="checkbox-group">
                    <input
                      type="checkbox"
                      id="is_ac"
                      className="checkbox-input"
                      checked={roomFormData.is_ac}
                      onChange={(e) => setRoomFormData({ ...roomFormData, is_ac: e.target.checked })}
                    />
                    <label htmlFor="is_ac" className="form-label">❄️ Air Conditioned Room</label>
                  </div>
                </div>
              </div>

              <div className="beds-section">
                <div className="beds-section-title">Bed-wise Pricing</div>
                {roomFormData.beds.map((bed, index) => (
                  <div key={index} className="bed-form-row">
                    <div className="form-group">
                      <label className="form-label">Bed {bed.bed_number}</label>
                      <input
                        type="text"
                        className="form-input"
                        value={`Bed ${bed.bed_number}`}
                        disabled
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Rent Amount (₹) *</label>
                      <input
                        type="number"
                        className="form-input"
                        value={bed.rent_amount}
                        onChange={(e) => {
                          const newBeds = [...roomFormData.beds];
                          newBeds[index].rent_amount = e.target.value;
                          setRoomFormData({ ...roomFormData, beds: newBeds });
                        }}
                        required
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Status</label>
                      <select
                        className="form-select"
                        value={bed.bed_status}
                        onChange={(e) => {
                          const newBeds = [...roomFormData.beds];
                          newBeds[index].bed_status = e.target.value;
                          setRoomFormData({ ...roomFormData, beds: newBeds });
                        }}
                      >
                        <option value="available">Available</option>
                        <option value="reserved">Reserved</option>
                        <option value="maintenance">Maintenance</option>
                      </select>
                    </div>
                  </div>
                ))}
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddRoomModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  ➕ Add Room
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Room Modal */}
      {showEditRoomModal && selectedRoom && (
        <div className="modal-overlay" onClick={() => setShowEditRoomModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">✏️ Edit Room</h2>
              <button className="modal-close" onClick={() => setShowEditRoomModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleUpdateRoom}>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Room Number</label>
                    <input
                      type="text"
                      className="form-input"
                      value={roomFormData.room_number}
                      onChange={(e) => setRoomFormData({ ...roomFormData, room_number: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Floor Number</label>
                    <input
                      type="number"
                      className="form-input"
                      value={roomFormData.floor_number}
                      onChange={(e) => setRoomFormData({ ...roomFormData, floor_number: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Block Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={roomFormData.block_name}
                      onChange={(e) => setRoomFormData({ ...roomFormData, block_name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Room Type *</label>
                    <select
                      className="form-select"
                      value={roomFormData.room_type}
                      onChange={(e) => setRoomFormData({ ...roomFormData, room_type: e.target.value })}
                      required
                    >
                      <option value="single">Single</option>
                      <option value="double">Double</option>
                      <option value="triple">Triple</option>
                      <option value="four_sharing">Four Sharing</option>
                      <option value="dormitory">Dormitory</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Room Status *</label>
                    <select
                      className="form-select"
                      value={roomFormData.room_status}
                      onChange={(e) => setRoomFormData({ ...roomFormData, room_status: e.target.value })}
                      required
                    >
                      <option value="active">Active</option>
                      <option value="maintenance">Maintenance</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  <div className="form-group full-width">
                    <label className="form-label">Room Facilities</label>
                    <textarea
                      className="form-textarea"
                      value={roomFormData.room_facilities}
                      onChange={(e) => setRoomFormData({ ...roomFormData, room_facilities: e.target.value })}
                    />
                  </div>
                  <div className="form-group full-width">
                    <div className="checkbox-group">
                      <input
                        type="checkbox"
                        id="edit_is_ac"
                        className="checkbox-input"
                        checked={roomFormData.is_ac}
                        onChange={(e) => setRoomFormData({ ...roomFormData, is_ac: e.target.checked })}
                      />
                      <label htmlFor="edit_is_ac" className="form-label">❄️ Air Conditioned Room</label>
                    </div>
                  </div>
                </div>

                <div className="beds-section">
                  <div className="beds-section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>🛏️ Manage Beds ({roomFormData.beds?.length || 0})</span>
                  </div>

                  {/* Bed List */}
                  {roomFormData.beds && roomFormData.beds.map((bed, index) => (
                    <div key={bed.id || index} className="bed-form-row">
                      <div className="form-group">
                        <label className="form-label">Bed Number</label>
                        <div style={{ padding: '14px', background: '#f8f9fa', borderRadius: '12px', fontWeight: 'bold' }}>
                          Bed {bed.bed_number}
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Status</label>
                        <div className={`bed-status-badge status-${bed.bed_status}`} style={{ display: 'inline-block', padding: '8px 12px' }}>
                          {bed.bed_status.toUpperCase()}
                        </div>
                        {bed.student_name && (
                          <div style={{ fontSize: '12px', marginTop: '4px', color: '#666' }}>
                            👤 {bed.student_name}
                          </div>
                        )}
                      </div>
                      <div className="form-group" style={{ justifyContent: 'flex-end' }}>
                        <label className="form-label">&nbsp;</label>
                        {bed.bed_status === 'available' ? (
                          <button
                            type="button"
                            className="btn btn-secondary"
                            style={{ color: '#dc3545', borderColor: '#dc3545' }}
                            onClick={() => handleDeleteBedFromRoom(bed.id)}
                          >
                            🗑️ Remove
                          </button>
                        ) : (
                          <span style={{ fontSize: '12px', color: '#999', alignSelf: 'center' }}>
                            Cannot remove occupied bed
                          </span>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Add New Bed Form */}
                  <div className="bed-form-row" style={{ marginTop: '20px', background: '#e8f5e9', borderColor: '#81c784' }}>
                    <div className="form-group">
                      <label className="form-label">New Bed Number</label>
                      <input
                        type="number"
                        className="form-input"
                        placeholder="e.g. 5"
                        id="new-bed-number"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Rent Amount</label>
                      <input
                        type="number"
                        className="form-input"
                        placeholder="e.g. 5000"
                        id="new-bed-rent"
                      />
                    </div>
                    <div className="form-group" style={{ justifyContent: 'flex-end' }}>
                      <label className="form-label">&nbsp;</label>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => {
                          const bedNumInput = document.getElementById('new-bed-number');
                          const rentInput = document.getElementById('new-bed-rent');
                          const bedNum = parseInt(bedNumInput.value, 10);
                          const rent = rentInput.value;

                          if (bedNum && !isNaN(bedNum) && rent) {
                            handleAddBedToRoom(bedNum, rent);
                            // Clear inputs after adding
                            bedNumInput.value = '';
                            rentInput.value = '';
                          } else {
                            alert('Please enter a valid numeric bed number and rent');
                          }
                        }}
                      >
                        ➕ Add Bed
                      </button>
                    </div>
                  </div>
                </div>

                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowEditRoomModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    💾 Save Room Details
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Bed Modal */}
      {showEditBedModal && selectedBed && (
        <div className="modal-overlay" onClick={() => setShowEditBedModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">✏️ Edit Bed</h2>
              <button className="modal-close" onClick={() => setShowEditBedModal(false)}>×</button>
            </div>
            <form onSubmit={handleUpdateBed}>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Bed Number</label>
                  <input
                    type="text"
                    className="form-input"
                    value={`Bed ${selectedBed.bed_number}`}
                    disabled
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Room Number</label>
                  <input
                    type="text"
                    className="form-input"
                    value={`${selectedBed.room_number} - Block ${selectedBed.block_name}`}
                    disabled
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Rent Amount (₹) *</label>
                  <input
                    type="number"
                    className="form-input"
                    value={bedFormData.rent_amount}
                    onChange={(e) => setBedFormData({ ...bedFormData, rent_amount: e.target.value })}
                    required
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Bed Status *</label>
                  <select
                    className="form-select"
                    value={bedFormData.bed_status}
                    onChange={(e) => setBedFormData({ ...bedFormData, bed_status: e.target.value })}
                    required
                  >
                    <option value="available">Available</option>
                    <option value="occupied">Occupied</option>
                    <option value="reserved">Reserved</option>
                    <option value="maintenance">Maintenance</option>
                  </select>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditBedModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  💾 Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </SuperAdminLayout>
  );
};

export default ManageRooms;